import { Logger, LoggerService } from '@Shared/logger';
import { SETTINGS } from '@Config';
import { SessionServerRepository } from '../repositories/session.repository';
import { CheckSessionStepResultData } from '@Modules/pipeline_module/steps';
import {
  Auth0Service,
  MNGSession,
  OptionsModule,
  ServerSession,
} from '@Modules/index';

export class SessionService extends SessionServerRepository {
  private static instance: SessionService;

  static get Instance() {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new SessionService();
    }
    return this.instance;
  }

  /**
   * The session SERVER (its API: GET/POST/DELETE) is opt-in per site. When off, the methods
   * below still run their local __MNG_Session logic and only skip the network call — this is
   * the single place that decision lives, so no caller has to gate the session server itself.
   *
   * The session server cannot accept requests from localhost. Local builds must skip the
   * network call regardless of what session.enabled says in the site config.
   */
  private get sessionEnabled(): boolean {
    if (SETTINGS.environment === 'Local') {
      return false;
    }
    return OptionsModule.Options.session.enabled === true;
  }

  async getAuth0Session(): Promise<CheckSessionStepResultData> {
    // Local recovery (storage / idToken) always runs. Only the session server API call is
    // opt-in per site: when the session feature is off we skip the network call, not the
    // whole check, so a locally stored __MNG_Session can still restore the user.
    // Prefer a locally stored session; only fall back to the session server when it's enabled.
    this.Logger.trace(
      'getAuth0Session: About to call getLocalStorageSession...',
    );
    let session = await this.getLocalStorageSession();
    this.Logger.trace('getAuth0Session: Session:', session);
    if (!session && this.sessionEnabled) {
      try {
        session = await this.getServerSession();
      } catch (error) {
        this.Logger.warn(
          'getAuth0Session: Session server unreachable during getAuth0Session; continuing without it',
          error,
        );
        session = undefined;
      }
    }
    const { idToken } = session ?? {};
    if (!idToken) {
      return { isAuthenticated: false };
    }
    // Decode the restored idToken into an AuthProfile so the UI (avatar, email)
    // has user data even when the Auth0 SDK has no local session to read from.
    const AuthProfile = Auth0Service.Instance.buildProfileFromIdToken(idToken);
    return { isAuthenticated: true, AuthProfile };
  }

  async getSession(): Promise<ServerSession> {
    this.Logger.trace('getSession: About to call getLocalStorageSession...');
    const localSession = await this.getLocalStorageSession();
    this.Logger.trace('getSession: Local session:', localSession);
    if (!localSession) {
      if (!this.sessionEnabled) {
        this.Logger.trace(
          'getSession: No local session and session server disabled, returning empty session',
        );
        return {} as ServerSession;
      }
      this.Logger.info(
        'getSession: No local session found, fetching from server.',
      );
      try {
        return await this.getServerSession();
      } catch (error) {
        // Entitlement check awaits getSession() before calling the entitler; a session-server
        // failure must not abort that path (or the rest of the login pipeline).
        this.Logger.warn(
          'getSession: Session server unreachable during getSession; continuing with empty session',
          error,
        );
        return {} as ServerSession;
      }
    }
    const serverSession: ServerSession = {
      accessToken: localSession.accessToken,
      adFree: localSession.adFree,
      entitlementLevel: localSession.entitlementLevel,
      entitlementSource: localSession.entitlementSource,
      expiration: localSession.expiration,
      idToken: localSession.idToken,
      entitlementToken: localSession.entitlementToken,
      isEntitled: localSession.isEntitled,
    };
    return serverSession;
  }

  async maybeUpdateWholeSession(mngSession: MNGSession): Promise<void> {
    try {
      this.Logger.info('maybeUpdateWholeSession: Updating whole session...');
      const currentSession =
        this.getLocalStorageSession() as unknown as MNGSession;
      const updatedSession: MNGSession = {
        accessToken:
          mngSession.accessToken ?? currentSession.accessToken ?? null,
        idToken: mngSession.idToken ?? currentSession.idToken ?? null,
        entitlementToken:
          mngSession.entitlementToken ??
          currentSession.entitlementToken ??
          null,
        expiration: mngSession.expiration ?? currentSession.expiration ?? null,
        entitlementSource:
          mngSession.entitlementSource ??
          currentSession.entitlementSource ??
          null,
        isEntitled: mngSession.isEntitled ?? currentSession.isEntitled ?? null,
        adFree: mngSession.adFree ?? currentSession.adFree ?? null,
        entitlementLevel:
          mngSession.entitlementLevel ??
          currentSession.entitlementLevel ??
          null,
      };

      this.Logger.info('maybeUpdateWholeSession: Session Update Trace:', {
        currentSession,
        mngSession,
        updatedSession,
      });

      super.saveLocalStorageSession(JSON.stringify(updatedSession));

      if (!this.sessionEnabled) {
        this.Logger.trace(
          'maybeUpdateWholeSession: Session server disabled, saved local session only (skipping updateServerSession)',
        );
        return;
      }

      if (JSON.stringify(currentSession) !== JSON.stringify(updatedSession)) {
        const updatedServerSession: ServerSession = {
          accessToken: updatedSession.accessToken,
          adFree: updatedSession.adFree,
          entitlementLevel: updatedSession.entitlementLevel,
          entitlementSource: updatedSession.entitlementSource,
          expiration: updatedSession.expiration,
          isEntitled: updatedSession.isEntitled,
          idToken: updatedSession.idToken,
          entitlementToken: updatedSession.entitlementToken,
        };
        await this.updateServerSession(updatedServerSession);
      }
    } catch (error) {
      this.Logger.error(
        'clearSession: Unable to save session:',
        error instanceof Error ? error.message : error,
      );
    }
  }

  /**
   * Callers own this explicitly — AuthModule.logout(), ClearUserDataStep and
   * ClearEntitlementCacheStep each call it once for their own flow, so a normal logout
   * issues a single DELETE.
   *
   * This flag lets overlapping callers reuse one in-flight request. It is a safety net
   * against a flow calling twice, not the thing that keeps the count down.
   */
  private pendingClear: Promise<void> | null = null;

  async clearSession(): Promise<void> {
    if (this.pendingClear) {
      this.Logger.trace(
        'clearSession: clear already in flight, reusing it instead of sending another DELETE',
      );
      return this.pendingClear;
    }
    this.pendingClear = this.runClearSession().finally(() => {
      this.pendingClear = null;
    });
    return this.pendingClear;
  }

  private async runClearSession(): Promise<void> {
    // Purely a session server API call (DELETE) — no local step to run, so when the session
    // server is off this is simply a no-op.
    if (!this.sessionEnabled) {
      this.Logger.trace(
        'clearSession: Session server disabled, skipping clearSession (deleteServerSession)',
      );
      return;
    }
    this.Logger.info('clearSession: Deleting server session...');
    try {
      await this.deleteServerSession();
    } catch (error) {
      this.Logger.warn(
        'clearSession: Session server unreachable during clearSession; continuing',
        error,
      );
    }
  }

  async saveSession(session: MNGSession): Promise<void> {
    try {
      const currentSession = await this.getLocalStorageSession();
      const updatedSession: MNGSession = {
        ...currentSession,
        ...session,
      };
      super.saveLocalStorageSession(JSON.stringify(updatedSession));
      if (!this.sessionEnabled) {
        this.Logger.trace(
          'saveSession: Session server disabled, saved local session only (skipping updateServerSession)',
        );
        return;
      }
      await super.updateServerSession(updatedSession);
    } catch (error) {
      this.Logger.error(
        'saveSession: Unable to save session:',
        error instanceof Error ? error.message : error,
      );
    }
  }

  constructor() {
    super();
    this.Logger.trace('****  CTOR ****');
  }

  setup() {
    this.Logger.trace('setup');
  }
}
